import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { archiveApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PageHeader, TableShell } from '@/components/layout'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import { 
  Archive, 
  RotateCcw, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Loader2,
  FolderOpen,
  Trash2
} from 'lucide-react'
import { toast } from 'sonner'
import { formatLanguagePair, formatDate } from '@/lib/utils'

export const Route = createFileRoute('/archive')({
  component: ArchivePage,
})

/** 归档管理页：查看归档任务队列与已归档项目，并支持重试与恢复。 */
function ArchivePage() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  const [isBatchDeleteDialogOpen, setIsBatchDeleteDialogOpen] = useState(false)

  const { data: tasksResponse, isLoading: tasksLoading } = useQuery({
    queryKey: ['archive-tasks'],
    queryFn: () => archiveApi.getTasks(),
    // 归档是后台异步任务，15s 轮询足够感知状态变化，避免高频读 D1
    refetchInterval: 15000,
  })

  const { data: projectsResponse, isLoading: projectsLoading } = useQuery({
    queryKey: ['archived-projects'],
    queryFn: () => archiveApi.getProjects(),
    // 后台异步归档完成后，已归档项目列表也需及时刷新
    refetchInterval: 15000,
  })

  const retryTask = useMutation({
    mutationFn: (taskId: string) => archiveApi.retryTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archive-tasks'] })
      toast.success('任务重试成功')
    },
    onError: (err: any) => {
      toast.error(`重试失败: ${err.error || err.message || '未知错误'}`)
    },
  })

  const deleteTask = useMutation({
    mutationFn: (taskId: string) => archiveApi.deleteTask(taskId),
    onSuccess: (res: any) => {
      // 先清空选中列表：被删除的 task 可能还在 selectedTaskIds 里，
      // 不清空会导致 refetch 后 selectableTaskIds 与 selectedTaskIds 不一致
      setSelectedTaskIds([])
      queryClient.invalidateQueries({ queryKey: ['archive-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['archived-projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(res?.restored ? '任务已删除，项目已恢复' : '任务已删除')
    },
    onError: (err: any) => {
      toast.error(`删除失败: ${err.error || err.message || '未知错误'}`)
    },
  })

  const batchDeleteTasks = useMutation({
    mutationFn: (taskIds: string[]) => archiveApi.batchDeleteTasks(taskIds),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['archive-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['archived-projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelectedTaskIds([])
      const deletedCount = res?.data?.deletedCount || 0
      const restored = res?.data?.restoredProjectCount || 0
      toast.success(`已删除 ${deletedCount} 个任务记录${restored > 0 ? `，并恢复 ${restored} 个项目` : ''}`)
    },
    onError: (err: any) => {
      toast.error(`批量删除失败: ${err.error || err.message || '未知错误'}`)
    },
  })

  const batchRetryTasks = useMutation({
    mutationFn: (taskIds: string[]) => archiveApi.batchRetryTasks(taskIds),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['archive-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['archived-projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelectedTaskIds([])
      const retriedCount = res?.data?.retriedCount || 0
      toast.success(`已重新触发 ${retriedCount} 个归档任务，请稍候查看结果`)
    },
    onError: (err: any) => {
      toast.error(`批量重试失败: ${err.error || err.message || '未知错误'}`)
    },
  })

  const restoreProject = useMutation({
    mutationFn: (projectId: string) => archiveApi.restoreProject(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archived-projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('项目已恢复')
    },
    onError: (err: any) => {
      toast.error(`恢复失败: ${err.error || err.message || '未知错误'}`)
    },
  })

  const tasks = (tasksResponse as any)?.data || []
  const archivedProjects = (projectsResponse as any)?.data || []

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary" className="text-muted-foreground">
            <Clock className="h-3 w-3 mr-1" /> 等待中
          </Badge>
        )
      case 'processing':
        return (
          <Badge variant="default">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" /> 处理中
          </Badge>
        )
      case 'completed':
        return (
          <Badge variant="secondary">
            <CheckCircle2 className="h-3 w-3 mr-1" /> 已完成
          </Badge>
        )
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" /> 失败
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const pendingCount = tasks.filter((t: any) => t.status === 'pending').length
  const processingCount = tasks.filter((t: any) => t.status === 'processing').length
  const completedCount = tasks.filter((t: any) => t.status === 'completed').length
  const failedCount = tasks.filter((t: any) => t.status === 'failed').length

  const selectableTaskIds = tasks.map((t: any) => t.id)
  const isAllSelected = selectableTaskIds.length > 0 && selectableTaskIds.every((id: string) => selectedTaskIds.includes(id))
  const isIndeterminate = selectedTaskIds.length > 0 && !isAllSelected

  if (tasksLoading || projectsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Archive className="h-6 w-6" />
            归档管理
          </span>
        }
        description="管理已归档项目和归档任务队列"
      />

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-xs text-muted-foreground">等待中</CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-4 pb-3">
            <div className="text-2xl font-bold">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-xs text-muted-foreground">处理中</CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-4 pb-3">
            <div className="text-2xl font-bold">{processingCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-xs text-muted-foreground">已完成</CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-4 pb-3">
            <div className="text-2xl font-bold">{completedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-xs text-muted-foreground">失败</CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-4 pb-3">
            <div className="text-2xl font-bold">{failedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* 归档任务队列 */}
      <TableShell
        title={
          <span className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            归档任务队列
          </span>
        }
        actions={
          selectedTaskIds.length > 0 && (user?.role === 'founder' || user?.role === 'pm') ? (
            <div className="flex items-center gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  if (import.meta.env.DEV) console.debug('[Archive] 批量重试：', { taskIds: selectedTaskIds })
                  batchRetryTasks.mutate(selectedTaskIds)
                }}
                disabled={batchRetryTasks.isPending}
              >
                {batchRetryTasks.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                <span className="ml-1">重试选中 ({selectedTaskIds.length})</span>
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  if (import.meta.env.DEV) console.debug('[Archive] 打开批量删除确认：', { taskIds: selectedTaskIds })
                  setIsBatchDeleteDialogOpen(true)
                }}
                disabled={batchDeleteTasks.isPending}
              >
                {batchDeleteTasks.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
                <span className="ml-1">删除选中 ({selectedTaskIds.length})</span>
              </Button>
            </div>
          ) : undefined
        }
      >
        {tasks.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">暂无归档任务</div>
        ) : (
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={isIndeterminate ? 'indeterminate' : isAllSelected}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedTaskIds(selectableTaskIds)
                        } else {
                          setSelectedTaskIds([])
                        }
                      }}
                      aria-label="全选"
                    />
                  </TableHead>
                  <TableHead>项目</TableHead>
                  <TableHead>客户</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>坚果云路径</TableHead>
                  <TableHead>创建时间</TableHead>
                  <TableHead>完成时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task: any) => (
                  <TableRow key={task.id}>
                    <TableCell className="w-10">
                      <Checkbox
                        checked={selectedTaskIds.includes(task.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedTaskIds([...selectedTaskIds, task.id])
                          } else {
                            setSelectedTaskIds(selectedTaskIds.filter((id) => id !== task.id))
                          }
                        }}
                        aria-label="选择任务"
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {task.project?.fileName || '-'}
                    </TableCell>
                    <TableCell>
                      {task.project?.client?.name || '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(task.status)}</TableCell>
                    <TableCell>
                      {task.nutstorePath ? (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground max-w-[200px] truncate">
                          <FolderOpen className="h-3 w-3 flex-shrink-0" />
                          <span title={task.nutstorePath}>{task.nutstorePath}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {task.createdAt ? formatDate(task.createdAt) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {task.completedAt ? formatDate(task.completedAt) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {(task.status === 'failed' || task.status === 'pending') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (import.meta.env.DEV) console.debug('[Archive] 重试归档任务：', { taskId: task.id })
                            retryTask.mutate(task.id)
                          }}
                          disabled={retryTask.isPending}
                        >
                          {retryTask.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3 w-3" />
                          )}
                          <span className="ml-1">重试</span>
                        </Button>
                      )}
                      {task.errorMessage && (
                        <span className="text-xs text-destructive ml-2" title={task.errorMessage}>
                          错误
                        </span>
                      )}
                      {(user?.role === 'founder' || user?.role === 'pm') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            if (import.meta.env.DEV) console.debug('[Archive] 删除归档任务：', { taskId: task.id })
                            if (window.confirm('确定删除该归档任务记录？')) {
                              deleteTask.mutate(task.id)
                            }
                          }}
                          disabled={deleteTask.isPending}
                        >
                          {deleteTask.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                          <span className="ml-1">删除</span>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
          </Table>
        )}
      </TableShell>

      {/* 已归档项目 */}
      <TableShell
        title={
          <span className="flex items-center gap-2">
            <Archive className="h-4 w-4" />
            已归档项目 ({archivedProjects.length})
          </span>
        }
      >
        {archivedProjects.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">暂无已归档项目</div>
        ) : (
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>文件名</TableHead>
                  <TableHead>客户</TableHead>
                  <TableHead>语言对</TableHead>
                  <TableHead>词数</TableHead>
                  <TableHead>归档时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {archivedProjects.map((project: any) => (
                  <TableRow key={project.id}>
                    <TableCell className="font-medium max-w-[300px] truncate">
                      {project.fileName}
                    </TableCell>
                    <TableCell>{project.client?.name || '-'}</TableCell>
                    <TableCell>
                      {formatLanguagePair(project.sourceLanguage, project.targetLanguage)}
                    </TableCell>
                    <TableCell>{project.sourceWordCount ?? project.rawWordCount}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {project.archivedAt ? formatDate(project.archivedAt) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {(user?.role === 'founder' || user?.role === 'pm') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (import.meta.env.DEV) console.debug('[Archive] 恢复归档项目：', { id: project.id })
                            restoreProject.mutate(project.id)
                          }}
                          disabled={restoreProject.isPending}
                        >
                          {restoreProject.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RotateCcw className="h-3 w-3" />
                          )}
                          <span className="ml-1">恢复</span>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
          </Table>
        )}
      </TableShell>

      {/* 批量删除确认对话框 */}
      <Dialog open={isBatchDeleteDialogOpen} onOpenChange={setIsBatchDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除归档任务</DialogTitle>
            <DialogDescription>
              确定删除选中的 {selectedTaskIds.length} 个归档任务记录？
              <br />
              未成功归档的对应项目将自动恢复为「已完成」状态，已成功的归档记录删除后项目仍保持归档。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsBatchDeleteDialogOpen(false)}
              disabled={batchDeleteTasks.isPending}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                if (import.meta.env.DEV) console.debug('[Archive] 确认批量删除：', { taskIds: selectedTaskIds })
                batchDeleteTasks.mutate(selectedTaskIds)
                setIsBatchDeleteDialogOpen(false)
              }}
              disabled={batchDeleteTasks.isPending}
            >
              {batchDeleteTasks.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Trash2 className="h-3 w-3 mr-1" />
              )}
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
